import { useEffect, useState } from 'react';
import { getYouTubeShortUrlHint } from '@/lib/urlUtils';

type AspectRatio = '9/16' | '16/9';

/**
 * Detects whether a YouTube URL is a Short and returns the appropriate aspect ratio.
 *
 * Strategy:
 * 1. Synchronously check URL hints (/shorts/ path, UUSH/UUPS/UULF/UULP list param)
 * 2. If hint is conclusive ('short' → '9/16', 'long' → '16/9'), return immediately — no API call
 * 3. If hint is 'unknown', default to '9/16' (safe: a Short in 16:9 looks worse than
 *    a long video in 9/16) and fire an API call to confirm; update to '16/9' if long_form
 * 4. Non-YouTube URLs always return '9/16' (TikTok/Instagram are always vertical here)
 */
export function useYouTubeShortDetection(url: string): AspectRatio {
  const isYouTube =
    url.includes('youtube.com') || url.includes('youtu.be');

  const hint = isYouTube ? getYouTubeShortUrlHint(url) : 'unknown';

  const initialRatio: AspectRatio =
    hint === 'long' ? '16/9' : '9/16';

  const [aspectRatio, setAspectRatio] = useState<AspectRatio>(initialRatio);
  const needsApiCall = isYouTube && hint === 'unknown';

  useEffect(() => {
    if (!needsApiCall) return;

    const controller = new AbortController();

    fetch(`/api/youtube/video-type?url=${encodeURIComponent(url)}`, {
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) return null;
        return res.json() as Promise<{ videoType: string }>;
      })
      .then((data) => {
        if (data?.videoType === 'long_form') {
          setAspectRatio('16/9');
        }
      })
      .catch((err) => {
        if (err?.name !== 'AbortError') {
          console.warn('[useYouTubeShortDetection] API call failed:', err);
        }
      });

    return () => controller.abort();
  }, [url, needsApiCall]);

  return aspectRatio;
}
